import { formatNumberValue } from '../../common/numberFormat';
import dayjs from 'dayjs';
import {
  DASHBOARD_DATA_STATE,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';

export const DASHBOARD_DATE_RANGE = Object.freeze({
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_60_DAYS: 'last_60_days',
  LAST_12_MONTHS: 'last_12_months',
  CUSTOM: 'custom'
});

export const DASHBOARD_DATE_RANGE_OPTIONS = Object.freeze([
  { value: DASHBOARD_DATE_RANGE.LAST_7_DAYS, label: 'Last 7 days' },
  { value: DASHBOARD_DATE_RANGE.LAST_30_DAYS, label: 'Last 30 days' },
  { value: DASHBOARD_DATE_RANGE.LAST_60_DAYS, label: 'Last 60 days' },
  { value: DASHBOARD_DATE_RANGE.LAST_12_MONTHS, label: 'Last 12 months' },
  { value: DASHBOARD_DATE_RANGE.CUSTOM, label: 'Custom dates' }
]);

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAXIMUM_DATE_RANGE_DAYS = 366;

const validCalendarDate = (value) => {
  if (!ISO_DATE_PATTERN.test(value ?? '')) return null;
  const date = dayjs(value);
  return date.isValid() && date.format('YYYY-MM-DD') === value ? date : null;
};

export const resolveDashboardDateRange = (
  selection,
  customFromDate,
  customToDate,
  referenceDate = dayjs()
) => {
  const today = dayjs(referenceDate);
  if (!today.isValid()) return null;

  if (selection === DASHBOARD_DATE_RANGE.CUSTOM) {
    const fromDate = validCalendarDate(customFromDate);
    const toDate = validCalendarDate(customToDate);
    if (
      !fromDate ||
      !toDate ||
      fromDate.isAfter(toDate, 'day') ||
      toDate.diff(fromDate, 'day') + 1 > MAXIMUM_DATE_RANGE_DAYS
    ) {
      return null;
    }

    return { fromDate: customFromDate, toDate: customToDate };
  }

  const daysBySelection = {
    [DASHBOARD_DATE_RANGE.LAST_7_DAYS]: 7,
    [DASHBOARD_DATE_RANGE.LAST_30_DAYS]: 30,
    [DASHBOARD_DATE_RANGE.LAST_60_DAYS]: 60
  };
  const days = daysBySelection[selection];
  const fromDate = days
    ? today.subtract(days - 1, 'day')
    : today.subtract(365, 'day');

  return {
    fromDate: fromDate.format('YYYY-MM-DD'),
    toDate: today.format('YYYY-MM-DD')
  };
};

export const dashboardMetricHasData = (latestMetric) =>
  latestMetric?.dataState === DASHBOARD_DATA_STATE.AVAILABLE &&
  latestMetric.value !== null &&
  latestMetric.value !== undefined &&
  Number.isFinite(Number(latestMetric.value));

export const formatDashboardMetricValue = (metric, latestMetric) => {
  if (!dashboardMetricHasData(latestMetric)) return 'No data';
  if (metric.fieldSource && metric.fieldConfig) return formatNumberValue(latestMetric.value, metric.fieldConfig);

  const numericValue = Number(latestMetric.value);
  const decimalPlaces =
    metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER
      ? 0
      : Math.min(6, Math.max(0, Number(metric.decimalPlaces) || 0));
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(numericValue);
  const unit =
    metric.unitLabel ||
    (metric.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE ? '%' : '');

  if (!unit) return formatted;
  return unit === '%' ? `${formatted}%` : `${formatted} ${unit}`;
};

export const buildVisualizationChartData = (
  visualization,
  seriesMetrics = []
) => {
  const metricsById = new Map(
    seriesMetrics.map((metric) => [metric.metricDefinitionId, metric])
  );
  const rowsByDate = new Map();

  [...(visualization?.series ?? [])]
    .sort((left, right) => left.seriesOrder - right.seriesOrder)
    .forEach((series) => {
      const metric = metricsById.get(series.metricDefinitionId);
      (metric?.points ?? []).forEach((point) => {
        const row = rowsByDate.get(point.effectiveDate) ?? {
          effectiveDate: point.effectiveDate
        };
        row[series.metricDefinitionId] = point.value;
        rowsByDate.set(point.effectiveDate, row);
      });
    });

  return [...rowsByDate.values()].sort((left, right) =>
    left.effectiveDate.localeCompare(right.effectiveDate)
  );
};

export const visualizationHasData = (visualization, chartData) =>
  chartData.some((row) =>
    (visualization?.series ?? []).some(
      (series) =>
        row[series.metricDefinitionId] !== null &&
        row[series.metricDefinitionId] !== undefined
    )
  );
